<script lang="ts" setup>
import { computed, PropType, ref } from "vue";
import { ParametersTemplateModel, TaskParameterModel, TaskParameterTypeEnum } from "@/components";
import { TaskParametersTable, TaskParametersInput } from "../components";
import {
  TirPmPopover,
  PopoverPositionEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmInput,
} from "tir-components";

/**Параметры */
const props = defineProps({
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Список шаблонов */
  templates: { type: Array as PropType<ParametersTemplateModel[]> },
  /**Редактируемые параметры */
  editParameters: { type: Array as PropType<TaskParameterModel[]> },
  /**Заблокировано создание шаблона */
  createTemplateDisabled: { type: Boolean },
  /**Заголовок параметра */
  title: { type: String, default: "" },
  /**Имя области */
  namespaceName: { type: String },
});
/**События */
const emit = defineEmits<{
  /**Добавление параметра */
  upsert: [parameter: TaskParameterModel];
  /**Очистка неиспользуемых параметров */
  clearUnused: [];
  /**Создание нового шаблона */
  createTemplate: [template: ParametersTemplateModel];
  /**Выбор шаблона */
  selectTemplate: [template: TaskParameterModel[]];
}>();

/**Объект для демонстрации добавления тега */
const upsertParameter = ref(new TaskParameterModel());
/**Имя нового шаблона */
const templateName = ref("");
/**Компонент поповер */
const $settingsPopover = ref<InstanceType<typeof TirPmPopover>>();

/**Блокировка кнопки очистки, если все параметры используются */
const isClearUnusedDisabled = computed(() => props.editParameters.every((p) => p.InUse));

/**Добавление параметра */
const onUpsertParameter = (parameter: TaskParameterModel) => {
  emit("upsert", new TaskParameterModel(parameter));
  upsertParameter.value = new TaskParameterModel({ Color: upsertParameter.value.Color });
};
/**Очистка не используемых параметров */
const clearUnusedParameters = () => {
  emit("clearUnused");
};
/**Создать шаблон */
const createTemplate = () => {
  emit(
    "createTemplate",
    new ParametersTemplateModel({
      Name: templateName.value,
      Data: JSON.stringify(props.editParameters),
      Type: props.type,
    }),
  );
  closePopover();
};
/**Закрыть поповер */
const closePopover = () => {
  setTimeout(() => $settingsPopover.value?.close?.());
};
/**Выбор шаблона */
const selectTemplate = (template: ParametersTemplateModel) => {
  emit("selectTemplate", template.ParsedData);
  upsertParameter.value = new TaskParameterModel({ Color: upsertParameter.value.Color });
};
</script>
<template>
  <div class="parameters-edit-form">
    <TaskParametersInput
      v-model:upsertParameter="upsertParameter"
      hide-templates
      :type="type"
      :templates="templates"
      :ignoreParameters="editParameters"
      :title="title"
      @upsert="onUpsertParameter"
      @selectTemplate="selectTemplate"
    />
    <div class="parameters-edit-form_controls">
      <TirPmPopover
        disable-content-blur
        is-no-teleport
        width="22rem"
        ref="$settingsPopover"
        :position="PopoverPositionEnum.Bottom"
        :is-disabled="createTemplateDisabled"
      >
        <template #default>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :size="TirPmButtonSizeEnum.Small"
          >
            Сохранить как шаблон
          </TirPmButton>
        </template>
        <template #content>
          <div class="parameters-edit-form_create-template">
            <TirPmInput
              v-model="templateName"
              :isWithHint="false"
              label="Название шаблона"
              placeholder="Шаблон"
              ref="$nameInput"
            />
            <div class="parameters-edit-form_create-template_buttons">
              <TirPmButton
                :variant="TirPmButtonVariantEnum.PrimaryState"
                :size="TirPmButtonSizeEnum.Small"
                :is-disabled="!templateName"
                @click="createTemplate"
              >
                Сохранить
              </TirPmButton>
              <TirPmButton
                :variant="TirPmButtonVariantEnum.LinkState"
                :size="TirPmButtonSizeEnum.Small"
                @click="closePopover"
              >
                Отмена
              </TirPmButton>
            </div>
          </div>
        </template>
      </TirPmPopover>
      <TirPmButton
        :variant="TirPmButtonVariantEnum.LinkState"
        :size="TirPmButtonSizeEnum.Small"
        :is-disabled="isClearUnusedDisabled"
        @click="clearUnusedParameters"
      >
        Очистить все
      </TirPmButton>
    </div>
    <TaskParametersTable
      :title="title"
      :parameters="editParameters"
      :type="type"
      :namespaceName="namespaceName"
    />
  </div>
</template>
<style lang="scss" scoped>
.parameters-edit-form {
  display: flex;
  flex-direction: column;
  &_controls {
    display: flex;
    gap: 0.25rem;
    margin: 2.25rem 0 0.75rem;
  }
  &_create-template {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    &_buttons {
      display: flex;
      gap: 0.5rem;
    }
  }
}
</style>
